import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Fade,
  InputAdornment,
  IconButton,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Visibility from "@mui/icons-material/VisibilityRounded";
import VisibilityOff from "@mui/icons-material/VisibilityOffRounded";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase/firebase";
import {
  doSignInWithEmailAndPassword,
} from "../../firebase/auth";
import linguistApi, { waitForLinguistAuthUser } from "services/linguistApi";
import { usePageTitle } from "hooks/usePageTitle";

const LinguistLogin = () => {
  usePageTitle("Linguist Login");

  const theme = useTheme();
  const navigate = useNavigate();
  const [fadeIn, setFadeIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Forgot-password UI state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(email);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFadeIn(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const checkExistingSession = async () => {
      if (!auth.currentUser) return;

      try {
        await linguistApi.get("/me");
        navigate("/linguist-portal", { replace: true });
      } catch (_) {
        // Not a linguist session, stay on login page.
      }
    };

    checkExistingSession();
  }, [navigate]);

  const recordLoginHistory = async (success, errorCode = null) => {
    try {
      let ipAddress = "";
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        ipAddress = data?.ip || "";
      } catch (_) {
        ipAddress = "";
      }

      await linguistApi.post("/login-history", {
        Email: email.trim(),
        LoginAt: new Date().toISOString(),
        Success: success,
        IPAddress: ipAddress,
        UserAgent: window.navigator.userAgent,
        ErrorCode: errorCode,
      });
    } catch (historyError) {
      console.warn("Unable to record linguist login history:", historyError);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      await doSignInWithEmailAndPassword(email.trim(), password);

      const currentUser = auth.currentUser || (await waitForLinguistAuthUser(5000));
      if (!currentUser) {
        throw new Error("Linguist auth session not available after sign-in");
      }

      // Validate linguist account access before entering portal pages.
      await linguistApi.get("/me");

      // Keep sign-in success independent from telemetry availability.
      recordLoginHistory(true).catch(() => {});
      navigate("/linguist-portal", { replace: true });
    } catch (loginError) {
      const status = loginError?.response?.status;
      const accountDisabled = loginError?.response?.data?.accountDisabled;
      const errorCode = loginError?.code || loginError?.response?.data?.error || "Login Failed";

      await recordLoginHistory(false, errorCode);

      if (status === 403 && accountDisabled) {
        setError(
          "Your account has been disabled by an admin, please reach out to your manager for further details",
        );
      } else {
        setError("Sorry, email or password is incorrect. Please retry.");
      }

      try {
        await signOut(auth);
      } catch (_) {
        // ignore
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setForgotLoading(true);
    setForgotError("");
    setForgotMessage("");
    try {
      const res = await linguistApi.post("/forgot-password", {
        email: forgotEmail.trim(),
      });
      setForgotMessage(
        res.data?.message ||
          "If that email is registered, a password reset link has been sent.",
      );
    } catch {
      setForgotError("Unable to send reset email. Please try again later.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !loading && isValidEmail && password) {
      handleLogin();
    }
  };

  const getForm = () => (
    <Box
      width="100%"
      height="auto"
      display="flex"
      flexDirection="column"
      sx={{
        px: { xs: 2, sm: 4, md: 8, xl: 4 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
            color: theme.palette.primary.main,
            textAlign: "center",
          }}
        >
          Linguist Portal
        </Typography>
      </Box>

      <Typography
        variant="body1"
        sx={{
          color: theme.palette.text.secondary,
          textAlign: "center",
          mb: 2,
        }}
      >
        Sign in to view assignments and update your profile.
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {message ? <Alert severity="success" sx={{ mb: 1.5 }}>{message}</Alert> : null}

      <TextField
        label="Email"
        variant="outlined"
        fullWidth
        margin="normal"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        onKeyDown={handleKeyDown}
        disabled={loading}
        autoComplete="email"
        InputProps={{
          endAdornment: loading && (
            <InputAdornment
              position="end"
              sx={{
                mr: 1,
                color: theme.palette.primary.main,
              }}
            >
              <CircularProgress size={20} color="inherit" />
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: email
                ? isValidEmail
                  ? theme.palette.primary.main
                  : theme.palette.error.main
                : theme.palette.text.primary,
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "1.2rem",
          },
          "& .MuiInputLabel-root": {
            fontSize: "1.05rem",
          },
          "label + &": {
            marginTop: theme.spacing(4),
          },
        }}
      />

      <TextField
        label="Password"
        variant="outlined"
        fullWidth
        margin="normal"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        type={showPassword ? "text" : "password"}
        disabled={loading}
        autoComplete="current-password"
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "1.2rem",
          },
          "& .MuiInputLabel-root": {
            fontSize: "1.05rem",
          },
          "label + &": {
            marginTop: theme.spacing(2),
          },
        }}
      />

      <Box sx={{ textAlign: "right", mt: 0.75 }}>
        <Link
          component="button"
          variant="body2"
          onClick={(event) => {
            event.preventDefault();
            setShowForgot((value) => !value);
            setForgotMessage("");
            setForgotError("");
          }}
          disabled={loading}
          sx={{
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.5 : 1,
            fontSize: { xs: "0.9rem", sm: "1rem", md: "1.05rem" },
          }}
        >
          {showForgot ? "Back to Sign In" : "Forgot Password?"}
        </Link>
      </Box>

      {showForgot ? (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: "10px",
            border: `1px solid ${theme.palette.primary.border2}`,
            backgroundColor: theme.palette.background.alt,
          }}
        >
          <Typography variant="body2" sx={{ mb: 1.25 }}>
            Enter your email and we will send a linguist portal reset link.
          </Typography>
          {forgotError ? <Alert severity="error" sx={{ mb: 1 }}>{forgotError}</Alert> : null}
          {forgotMessage ? <Alert severity="success" sx={{ mb: 1 }}>{forgotMessage}</Alert> : null}
          {!forgotMessage ? (
            <>
              <TextField
                label="Your email address"
                value={forgotEmail}
                onChange={(event) => setForgotEmail(event.target.value)}
                fullWidth
                autoComplete="email"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: forgotEmail.trim()
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                    },
                  },
                }}
              />
              <Button
                onClick={handleForgotPassword}
                variant="outlined"
                disabled={forgotLoading || !forgotEmail.trim()}
                sx={{
                  mt: 1.25,
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                }}
              >
                {forgotLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Send Reset Link"
                )}
              </Button>
            </>
          ) : null}
        </Box>
      ) : null}

      <Box
        sx={{
          mt: { xs: 2, sm: 3, md: 4 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          size="large"
          onClick={handleLogin}
          disabled={loading || !isValidEmail || !password}
          sx={{
            borderRadius: "12px",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.buttonHover,
            },
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 1, sm: 1.25, md: 1.5 },
            fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
            fontWeight: "bold",
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Log In"}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Fade in={fadeIn} timeout={1000}>
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100vh"
        sx={{
          backgroundColor: theme.palette.background.alt,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: { xs: 16, sm: 24, md: 32 },
            left: { xs: "50%", sm: 24, md: 32 },
            transform: { xs: "translateX(-50%)", sm: "none" },
            width: { xs: "180px", sm: "240px", md: "360px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
          }}
        >
          <img
            src="/assets/ce-logo-full-h.svg"
            alt="CE Portal Logo"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            px: { xs: 2, sm: 3 },
          }}
        >
          <Grid
            container
            sx={{
              width: "100%",
              maxWidth: "1200px",
              minHeight: { xs: "auto", sm: "50vh" },
              backgroundColor: theme.palette.background.default,
              borderRadius: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
              boxShadow: 3,
              overflow: "hidden",
            }}
          >
            <Grid
              size={{ xs: 0, xl: 6 }}
              sx={{
                display: { xs: "none", xl: "flex" },
                pt: { xl: 4 },
                pb: { xl: 8 },
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
                minHeight: { xl: "300px" },
                borderRight: { xl: `1px solid ${theme.palette.divider}` },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.1,
                  scale: 0.75,
                  backgroundImage: 'url("/assets/ce-logo.svg")',
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  zIndex: 1,
                  animation: `zoomInOut 12s ease-in-out infinite`,
                  "@keyframes zoomInOut": {
                    "0%": {
                      transform: "scale(1)",
                    },
                    "50%": {
                      transform: "scale(0.85)",
                    },
                    "100%": {
                      transform: "scale(1)",
                    },
                  },
                }}
              />
            </Grid>

            <Grid
              size={{ xs: 12, xl: 6 }}
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {getForm()}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Fade>
  );
};

export default LinguistLogin;